// src/components/institution/manager/FacilitiesTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Building2, 
  MapPin, 
  Users, 
  Grid,
  List,
  Download,
  Eye,
  Maximize2,
  CheckCircle,
  XCircle,
  Clock,
  Filter
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';
import { FacilityForm } from '../forms/FacilityForm';

export const FacilitiesTab = ({ bookId, institutionData, onUpdate }) => {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadFacilities();
  }, [bookId]);

  useEffect(() => {
    applyFilters();
  }, [facilities, searchTerm, selectedType, selectedStatus]);

  const loadFacilities = async () => {
    try {
      const facilitiesData = await institutionService.getFacilities(bookId);
      setFacilities(facilitiesData);
    } catch (error) {
      console.error('Error loading facilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...facilities];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(facility =>
        facility.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        facility.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        facility.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(facility => facility.type === selectedType);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(facility => facility.status === selectedStatus);
    }

    setFilteredFacilities(filtered);
  };

  const handleSaveFacility = async (facilityData) => {
    try {
      if (editingFacility) {
        await institutionService.updateFacility(bookId, editingFacility.id, facilityData);
      } else {
        await institutionService.createFacility(bookId, facilityData);
      }
      
      await loadFacilities();
      await onUpdate();
      setShowForm(false);
      setEditingFacility(null);
    } catch (error) {
      console.error('Error saving facility:', error);
      alert('Failed to save facility');
    }
  };

  const handleDeleteFacility = async (facilityId) => {
    if (window.confirm('Are you sure you want to delete this facility?')) {
      try {
        await institutionService.deleteFacility(bookId, facilityId);
        await loadFacilities();
        await onUpdate();
      } catch (error) {
        console.error('Error deleting facility:', error);
        alert('Failed to delete facility');
      }
    }
  };

  const handleEditFacility = (facility) => {
    setEditingFacility(facility);
    setShowForm(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'text-green-600 bg-green-100';
      case 'maintenance': return 'text-yellow-600 bg-yellow-100';
      case 'closed': return 'text-red-600 bg-red-100';
      case 'under-construction': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return CheckCircle;
      case 'maintenance': return Clock;
      case 'closed': return XCircle;
      case 'under-construction': return Building2;
      default: return Building2;
    }
  };

  const types = [...new Set(facilities.map(f => f.type).filter(Boolean))];
  const statuses = ['operational', 'maintenance', 'closed', 'under-construction'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="h-6 w-6 text-indigo-600" />
            Facilities & Infrastructure
          </h2>
          <p className="text-gray-600">Manage campus buildings, rooms, and facilities</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Facility
          </button>
          
          <button
            onClick={() => institutionService.exportFacilities(bookId)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search facilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            {types.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>
                {status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${
                viewMode === 'grid' 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${
                viewMode === 'list' 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Facilities</p>
              <p className="text-2xl font-bold text-gray-900">{facilities.length}</p>
            </div>
            <Building2 className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Operational</p>
              <p className="text-2xl font-bold text-green-600">
                {facilities.filter(f => f.status === 'operational').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Maintenance</p>
              <p className="text-2xl font-bold text-yellow-600">
                {facilities.filter(f => f.status === 'maintenance').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Capacity</p>
              <p className="text-2xl font-bold text-blue-600">
                {facilities.reduce((sum, f) => sum + (f.capacity || 0), 0)}
              </p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Facilities List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : filteredFacilities.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Facilities Found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedType !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first facility'}
          </p>
          {!searchTerm && selectedType === 'all' && selectedStatus === 'all' && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            >
              Add Facility
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFacilities.map((facility) => {
            const StatusIcon = getStatusIcon(facility.status);
            
            return (
              <div key={facility.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                {/* Facility Image */}
                {facility.images && facility.images.length > 0 ? (
                  <div className="relative h-48 bg-gray-100">
                    <img
                      src={facility.images[0].url || facility.images[0]}
                      alt={facility.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(facility.status)}`}>
                        <StatusIcon className="h-3 w-3" />
                        {facility.status?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center">
                    <Building2 className="h-16 w-16 text-indigo-300" />
                  </div>
                )}

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{facility.name}</h3>
                      {facility.type && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {facility.type}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditFacility(facility)}
                        className="p-1 text-gray-400 hover:text-indigo-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFacility(facility.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {facility.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {facility.description}
                    </p>
                  )}

                  <div className="space-y-2">
                    {facility.location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{facility.location}</span>
                      </div>
                    )}
                    
                    {facility.capacity && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">Capacity: {facility.capacity}</span>
                      </div>
                    )}

                    {facility.size && (
                      <div className="flex items-center gap-2 text-sm">
                        <Maximize2 className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{facility.size} sq ft</span>
                      </div>
                    )}
                  </div>

                  {facility.amenities && facility.amenities.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex flex-wrap gap-1">
                        {facility.amenities.slice(0, 3).map((amenity, index) => (
                          <span
                            key={index}
                            className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded"
                          >
                            {amenity}
                          </span>
                        ))}
                        {facility.amenities.length > 3 && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            +{facility.amenities.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="space-y-4">
          {filteredFacilities.map((facility) => {
            const StatusIcon = getStatusIcon(facility.status);
            
            return (
              <div key={facility.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all">
                <div className="flex gap-6">
                  {/* Image */}
                  {facility.images && facility.images.length > 0 ? (
                    <div className="flex-shrink-0">
                      <img
                        src={facility.images[0].url || facility.images[0]}
                        alt={facility.name}
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-32 h-32 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg flex items-center justify-center">
                      <Building2 className="h-12 w-12 text-indigo-300" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{facility.name}</h3>
                        <div className="flex items-center gap-2">
                          {facility.type && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {facility.type}
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(facility.status)}`}>
                            <StatusIcon className="h-3 w-3" />
                            {facility.status?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditFacility(facility)}
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFacility(facility.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {facility.description && (
                      <p className="text-gray-600 text-sm mb-3">
                        {facility.description}
                      </p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {facility.location && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">{facility.location}</span>
                        </div>
                      )}
                      
                      {facility.capacity && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">Cap: {facility.capacity}</span>
                        </div>
                      )}

                      {facility.size && (
                        <div className="flex items-center gap-2 text-sm">
                          <Maximize2 className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600">{facility.size} sq ft</span>
                        </div>
                      )}
                    </div>

                    {facility.amenities && facility.amenities.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex flex-wrap gap-1">
                          {facility.amenities.map((amenity, index) => (
                            <span
                              key={index}
                              className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded"
                            >
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Facility Form Modal */}
      {showForm && (
        <FacilityForm
          bookId={bookId}
          facility={editingFacility}
          onSave={handleSaveFacility}
          onClose={() => {
            setShowForm(false);
            setEditingFacility(null);
          }}
        />
      )}
    </div>
  );
};